import React, { FC, useMemo, useState } from "react";
import { SortingOrder } from "@shared/types";
import { ColumnType } from "@shared/common/CardsList/types";
import {
  CardSortersProps,
  ExpandCollapseDirection,
} from "@shared/common/CardSorters/types";

interface useCardSortersProps<RecordType extends { key: string | number }> {
  CardSorters: FC<CardSortersProps>;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  keys: (string | number)[];
  expandedKeys?: (string | number)[];
  style?: any;
  columns: Omit<ColumnType<RecordType>, "render">[];
  data?: RecordType[];
  showExpandCollapse?: boolean;
  defaultSortFunction?: (a: RecordType, b: RecordType) => number;
}

const useCardSorters = <RecordType extends { key: string | number } = any>({
  CardSorters,
  onExpandAll,
  onCollapseAll,
  keys,
  expandedKeys,
  style,
  columns,
  data,
  showExpandCollapse,
  defaultSortFunction,
}: useCardSortersProps<RecordType>) => {
  const [currentExpandCollapseDirection, setCurrentExpandCollapse] = useState(
    ExpandCollapseDirection.EXPAND
  );
  const defaultSortColumn = useMemo(
    () => columns?.find((column) => column.defaultSortOrder),
    [columns]
  );
  const [sortOrder, setSortOrder] = useState<SortingOrder>(
    defaultSortColumn?.defaultSortOrder || SortingOrder.ASCEND
  );
  const [sortColumnKey, setSortColumnKey] = useState<
    string | number | undefined
  >(defaultSortColumn?.key);

  const expandAll = () => {
    setCurrentExpandCollapse(ExpandCollapseDirection.COLLAPSE);
    onExpandAll?.();
  };

  const collapseAll = () => {
    setCurrentExpandCollapse(ExpandCollapseDirection.EXPAND);
    onCollapseAll?.();
  };

  const onExpandCollapseAllClick = () => {
    if (expandedKeys?.length === 0) expandAll();
    else if (expandedKeys?.length === keys.length) collapseAll();
    else if (
      currentExpandCollapseDirection === ExpandCollapseDirection.COLLAPSE
    )
      collapseAll();
    else if (currentExpandCollapseDirection === ExpandCollapseDirection.EXPAND)
      expandAll();
  };

  const cardsSorters = (
    <CardSorters
      onExpandCollapseClick={onExpandCollapseAllClick}
      columns={columns}
      sortColumnKey={sortColumnKey}
      sortOrder={sortOrder}
      setSortColumnKey={setSortColumnKey}
      setSortOrder={setSortOrder}
      showExpandCollapse={showExpandCollapse}
      style={style}
    />
  );

  const sortFunction = useMemo(
    () =>
      columns.find((column) => column.key === sortColumnKey)?.sorter ||
      defaultSortFunction,
    [columns, sortColumnKey, defaultSortFunction]
  );

  const sortedData = useMemo(() => {
    if (sortFunction) {
      return data?.slice().sort((a, b) => sortFunction(a, b) * sortOrder);
    }
    return data;
  }, [data, sortFunction, sortOrder]);

  return { sortOrder, sortColumnKey, cardsSorters, sortedData };
};

export default useCardSorters;
